<template>
  <div class="history-autocomplete" style="position: relative;">
    <v-text-field
      v-if="variant === 'vuetify'"
      ref="field"
      :label="label"
      :value="value"
      :data-vv-name="dataVvName"
      autocomplete="off"
      @input="handleInput"
    ></v-text-field>
    <input
      v-else
      type="text"
      class="form-control"
      style="width: 100%; min-width: 0;"
      :placeholder="placeholder"
      :value="value"
      autocomplete="off"
      @input="handleInput($event.target.value)"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown="onKeydown"
    />

    <ul
      v-if="isOpen && filteredItems.length"
      ref="dropdown"
      class="history-autocomplete-list"
      :style="dropdownStyle"
    >
      <li
        v-for="(item, i) in filteredItems"
        :key="item"
        :class="{ 'history-autocomplete-item--active': i === highlightedIndex }"
        style="padding: 6px 12px; cursor: pointer;"
        @mousedown.prevent="selectItem(item)"
      >
        {{ item }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  props: {
    value: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    variant: { type: String, default: 'vuetify' },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    dataVvName: { type: String, default: '' },
    maxSuggestions: { type: Number, default: 50 }
  },

  data() {
    return {
      isOpen: false,
      highlightedIndex: -1,
      blurTimeoutId: null,
      dropdownStyle: {}
    }
  },

  computed: {
    filteredItems() {
      const search = (this.value || '').trim().toLowerCase()
      const seen = new Set()
      const deduped = []
      this.items.forEach(item => {
        const key = item.toLowerCase()
        if (!seen.has(key)) {
          seen.add(key)
          deduped.push(item)
        }
      })
      const matches = search
        ? deduped.filter(item => item.toLowerCase().includes(search))
        : deduped
      return matches.slice(0, this.maxSuggestions)
    }
  },

  watch: {
    isOpen(open) {
      if (open) {
        this.$nextTick(() => {
          this.attachDropdownToBody()
          this.updateDropdownPosition()
          window.addEventListener('scroll', this.updateDropdownPosition, true)
          window.addEventListener('resize', this.updateDropdownPosition)
        })
      } else {
        window.removeEventListener('scroll', this.updateDropdownPosition, true)
        window.removeEventListener('resize', this.updateDropdownPosition)
      }
    }
  },

  mounted() {
    if (this.variant === 'vuetify') {
      this.$nextTick(() => {
        const input = this.$refs.field && this.$refs.field.$el.querySelector('input')
        if (input) {
          this.nativeInput = input
          input.addEventListener('focus', this.handleFocus)
          input.addEventListener('blur', this.handleBlur)
          input.addEventListener('keydown', this.onKeydown)
        }
      })
    }
  },

  beforeDestroy() {
    if (this.blurTimeoutId) clearTimeout(this.blurTimeoutId)
    if (this.nativeInput) {
      this.nativeInput.removeEventListener('focus', this.handleFocus)
      this.nativeInput.removeEventListener('blur', this.handleBlur)
      this.nativeInput.removeEventListener('keydown', this.onKeydown)
    }
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
    window.removeEventListener('resize', this.updateDropdownPosition)
    if (this.$refs.dropdown && this.$refs.dropdown.parentNode) {
      this.$refs.dropdown.parentNode.removeChild(this.$refs.dropdown)
    }
  },

  methods: {
    // Moves the dropdown <ul> to document.body so it escapes any ancestor
    // with overflow-x/y set (e.g. the horizontally-scrollable bulk-order
    // table), which would otherwise clip it, and any dialog stacking context.
    attachDropdownToBody() {
      const dropdown = this.$refs.dropdown
      if (dropdown && dropdown.parentNode !== document.body) {
        document.body.appendChild(dropdown)
      }
    },

    updateDropdownPosition() {
      const dropdown = this.$refs.dropdown
      if (!dropdown) return
      const rect = this.$el.getBoundingClientRect()
      this.dropdownStyle = {
        position: 'fixed',
        top: rect.bottom + 'px',
        left: rect.left + 'px',
        width: rect.width + 'px',
        zIndex: 9999
      }
    },

    handleInput(newVal) {
      this.$emit('input', newVal)
      this.isOpen = true
      this.highlightedIndex = -1
    },

    handleFocus() {
      this.isOpen = true
    },

    handleBlur() {
      // @mousedown.prevent on each item is the primary defense against blur
      // firing before a click registers; this timeout is just a fallback.
      this.blurTimeoutId = setTimeout(() => {
        this.isOpen = false
        this.highlightedIndex = -1
      }, 150)
    },

    selectItem(item) {
      this.$emit('input', item)
      this.isOpen = false
      this.highlightedIndex = -1
    },

    onKeydown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.isOpen = true
        if (this.highlightedIndex < this.filteredItems.length - 1) this.highlightedIndex++
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (this.highlightedIndex > 0) this.highlightedIndex--
      } else if (e.key === 'Enter') {
        if (this.isOpen && this.highlightedIndex >= 0 && this.filteredItems[this.highlightedIndex]) {
          e.preventDefault()
          this.selectItem(this.filteredItems[this.highlightedIndex])
        }
      } else if (e.key === 'Escape') {
        this.isOpen = false
        this.highlightedIndex = -1
      }
    }
  }
}
</script>

<style scoped>
.history-autocomplete-list {
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}
.history-autocomplete-item--active {
  background: #f0f0f0;
}
.history-autocomplete-list li:hover {
  background: #f5f5f5;
}
</style>



// WEBPACK FOOTER //
// src/components/HistoryAutocomplete.vue